import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isNil } from 'lodash'
import cx from 'classnames'

import { MAX_IMAGE_SIZE } from 'config'

import { checkFileSize, fileToBase64, getFileFromURL } from 'lib/image'

import useGlobalConfirmBox from 'features/GlobalConfirmBox/hooks/useGlobalConfirmBox'

import ImageLoader from 'components/ImageLoader'
import Button from 'components/Button'
import CameraIcon from 'components/Icons/CameraIcon'
import ConfirmBox from 'components/ConfirmBox'

import './style.scss'

const placeholderUrl = '/images/transparent-bg.png'

const EditImage = ({
  id,
  url,
  alt,
  className,
  onSelect,
  defaultURL,
  reUpload,
}: {
  id: string
  url: string
  alt: string
  className: string
  onSelect: (file: File) => void
  defaultURL?: string
  reUpload?: boolean
}) => {
  const inputRef = useRef<HTMLInputElement>()
  const [image, setImage] = useState(url ?? defaultURL)
  const [imageFile, setImageFile] = useState(null)
  const [isConfirmActive, setIsConfirmActive] = useState(false)
  const { showErrorBox } = useGlobalConfirmBox()
  const { t } = useTranslation()

  useEffect(() => {
    if (image && reUpload) setImageFromURL(image)
  }, [])

  useEffect(() => {
    if (imageFile) {
      fileToBase64(imageFile).then(setImage)
    }
  }, [imageFile])

  const changeFile = (file: File, showError: boolean = true) => {
    if (file) {
      if (checkFileSize(file)) {
        onSelect(file)
        setImageFile(file)
      } else {
        if (showError) {
          showErrorBox({
            content: t('upload_image.maximum_size_limit_reached', {
              limit: MAX_IMAGE_SIZE,
            }),
          })
        } else {
          onSelect(null)
          setImage(defaultURL ?? placeholderUrl)
        }
      }
    }
  }

  const setImageFromURL = async (url: string) => {
    const file = await getFileFromURL(url)
    changeFile(file, false)
  }

  const onCancel = () => {
    inputRef.current.value = null
    setIsConfirmActive(false)
  }

  const onDelete = async () => {
    if (defaultURL && reUpload) {
      setImageFromURL(defaultURL)
    } else {
      onSelect(null)
      setImage(defaultURL ?? placeholderUrl)
    }
    inputRef.current.value = null
    setIsConfirmActive(false)
  }

  return (
    <>
      <div className={cx('EditImage', className)}>
        <ImageLoader
          className="EditImage__image"
          url={image}
          placeholderImage={defaultURL ?? placeholderUrl}
          alt={alt}
          enableRetry
        />

        <input
          ref={inputRef}
          accept=".png, .jpg, .jpeg, .gif"
          id={id}
          type="file"
          className="EditImage__input"
          onChange={(e) => changeFile(e.target?.files?.[0])}
        />
        <a
          className="EditImage__overlay"
          onClick={() => {
            setIsConfirmActive(true)
          }}
        >
          <CameraIcon />
        </a>
      </div>

      <ConfirmBox isActive={isConfirmActive} className="EditImage__confirm">
        <a className="ConfirmBox__overlay" onClick={onCancel} />

        <div className="ConfirmBox__wrapper">
          <div className="ConfirmBox__header">
            <h3 className="ConfirmBox__header__h3">
              {t('edit_profile_modal.image_alert.title')}
            </h3>

            <p className="ConfirmBox__header__p">
              {t('edit_profile_modal.image_alert.description')}
            </p>
          </div>

          <div className="ConfirmBox__body">
            {!isNil(url) && (
              <Button fluid onClick={onDelete} buttonStyle="ghost-red">
                {t('edit_profile_modal.image_alert.delete_image')}
              </Button>
            )}

            <Button
              fluid
              onClick={() => {
                inputRef.current?.click?.()
                setIsConfirmActive(false)
              }}
            >
              {t('edit_profile_modal.image_alert.select_image')}
            </Button>

            <Button fluid onClick={onCancel} buttonStyle="bordered-gray-light">
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      </ConfirmBox>
    </>
  )
}

export default EditImage
